import {
  type InfiniteData,
  type InfiniteQueryObserverResult,
  type InfiniteQueryObserverSuccessResult,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseSuspenseInfiniteQueryResult,
} from '@tanstack/react-query';

import { useOnline } from '@mint-lib/hooks';

// Overload 1: suspense: true (data is always defined)
export function useTypedInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    'placeholderData'
  > & {
    suspense: true;
    throwOnError?: boolean;
    placeholderData?: InfiniteData<TQueryFnData, TPageParam>;
  },
): UseSuspenseInfiniteQueryResult<TData, TError>;

// Overload 2: placeholderData provided (data is always defined)
export function useTypedInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    'placeholderData'
  > & {
    suspense?: false;
    throwOnError?: boolean;
    placeholderData: Exclude<
      UseInfiniteQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
      >['placeholderData'],
      undefined
    >;
  },
): InfiniteQueryObserverSuccessResult<TData, TError>;

// Overload 3: no suspense, no placeholderData (data can be undefined)
export function useTypedInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    'placeholderData'
  > & {
    suspense?: false;
    throwOnError?: boolean;
    placeholderData?: never;
  },
): InfiniteQueryObserverResult<TData, TError>;

// Implementation
export function useTypedInfiniteQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >,
):
  | InfiniteQueryObserverResult<TData, TError>
  | UseSuspenseInfiniteQueryResult<TData, TError> {
  const { throwOnError, ...restOptions } = options;

  const isOnline = useOnline();

  const queryResult = useInfiniteQuery<
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TPageParam
  >({
    throwOnError: (error, query) => {
      if (!throwOnError) {
        return false;
      }

      const notInCache = query.state.data === undefined;

      return Boolean(notInCache && isOnline);
    },
    ...restOptions,
  });

  return {
    ...queryResult,
    // the way placeholderData works is counterintuitive https://github.com/TanStack/query/issues/1712
    isLoading: queryResult.isPlaceholderData || queryResult.isLoading,
    isPending: queryResult.isPlaceholderData || queryResult.isPending,
  } as
    | InfiniteQueryObserverResult<TData, TError>
    | UseSuspenseInfiniteQueryResult<TData, TError>;
}
